import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationRequestCard = ({ request, onApprove, onReject, onMarkUnderReview, onViewDetails }) => {
  const getStatusBadge = () => {
    const badges = {
      pending: { bg: 'bg-warning/10', text: 'text-warning', label: 'Pending' },
      under_review: { bg: 'bg-primary/10', text: 'text-primary', label: 'Under Review' },
      approved: { bg: 'bg-success/10', text: 'text-success', label: 'Approved' },
      rejected: { bg: 'bg-error/10', text: 'text-error', label: 'Rejected' }
    };
    return badges?.[request?.status] || badges?.pending;
  };

  const getRoleBadge = () => {
    const badges = {
      candidate: { icon: 'UserCheck', bg: 'bg-primary/10', text: 'text-primary' },
      employer: { icon: 'Building2', bg: 'bg-accent/10', text: 'text-accent' }
    };
    return badges?.[request?.role] || badges?.candidate;
  };

  const getRiskBadge = () => {
    const badges = {
      low: { bg: 'bg-success/10', text: 'text-success', label: 'Low Risk' },
      medium: { bg: 'bg-warning/10', text: 'text-warning', label: 'Medium Risk' },
      high: { bg: 'bg-error/10', text: 'text-error', label: 'High Risk' }
    };
    return badges?.[request?.riskLevel] || badges?.low;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusBadge = getStatusBadge();
  const roleBadge = getRoleBadge();
  const riskBadge = getRiskBadge();

  return (
    <div className="bg-card rounded-2xl p-6 elevation-sm border border-border hover:border-primary/30 transition-all">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left Section - User Info */}
        <div className="flex-1 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-foreground">{request?.fullName}</h3>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge?.bg} ${statusBadge?.text}`}>
                  {statusBadge?.label}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-3">{request?.email}</p>
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleBadge?.bg} ${roleBadge?.text} flex items-center gap-1`}>
                  <Icon name={roleBadge?.icon} size={12} />
                  {request?.role}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${riskBadge?.bg} ${riskBadge?.text}`}>
                  {riskBadge?.label}
                </span>
                {request?.verificationScore && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-foreground">
                    Score: {request?.verificationScore}/100
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {request?.industry && (
              <div className="flex items-center gap-2">
                <Icon name="Briefcase" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{request?.industry}</span>
              </div>
            )}
            {request?.location && (
              <div className="flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{request?.location}</span>
              </div>
            )}
            {request?.experience && (
              <div className="flex items-center gap-2">
                <Icon name="Award" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{request?.experience} years</span>
              </div>
            )}
            {request?.companyName && (
              <div className="flex items-center gap-2">
                <Icon name="Building" size={16} className="text-muted-foreground" />
                <span className="text-sm text-foreground">{request?.companyName}</span>
              </div>
            )}
          </div>

          {/* Bio Preview */}
          {request?.bio && (
            <div className="bg-muted/50 rounded-xl p-4">
              <p className="text-sm text-foreground line-clamp-2">{request?.bio}</p>
            </div>
          )}

          {/* Timestamps */}
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="Calendar" size={14} />
              <span>Submitted: {formatDate(request?.submittedAt)}</span>
            </div>
            {request?.lastStatusChange && (
              <div className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                <span>Last Update: {formatDate(request?.lastStatusChange)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex flex-col gap-3 lg:w-48">
          <Button
            variant="outline"
            size="sm"
            iconName="FileText"
            iconPosition="left"
            onClick={() => onViewDetails(request)}
            className="w-full"
          >
            View Details
          </Button>

          {request?.status === 'pending' && (
            <>
              <Button
                variant="outline"
                size="sm"
                iconName="Eye"
                iconPosition="left"
                onClick={() => onMarkUnderReview(request)}
                className="w-full"
              >
                Mark Reviewing
              </Button>
              <Button
                variant="success"
                size="sm"
                iconName="Check"
                iconPosition="left"
                onClick={() => onApprove(request)}
                className="w-full"
              >
                Approve
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="X"
                iconPosition="left"
                onClick={() => onReject(request, 'Does not meet requirements')}
                className="w-full"
              >
                Reject
              </Button>
            </>
          )}

          {request?.status === 'under_review' && (
            <>
              <Button
                variant="success"
                size="sm"
                iconName="Check"
                iconPosition="left"
                onClick={() => onApprove(request)}
                className="w-full"
              >
                Approve
              </Button>
              <Button
                variant="destructive"
                size="sm"
                iconName="X"
                iconPosition="left"
                onClick={() => onReject(request, 'Does not meet requirements')}
                className="w-full"
              >
                Reject
              </Button>
            </>
          )}

          {(request?.status === 'approved' || request?.status === 'rejected') && (
            <div className={`p-3 rounded-xl text-center ${statusBadge?.bg}`}>
              <Icon 
                name={request?.status === 'approved' ? 'CheckCircle' : 'XCircle'} 
                size={24} 
                className={`mx-auto mb-2 ${statusBadge?.text}`} 
              />
              <p className={`text-sm font-medium ${statusBadge?.text}`}>
                {statusBadge?.label}
              </p>
              {request?.reviewedAt && (
                <p className="text-xs text-muted-foreground mt-1">
                  {formatDate(request?.reviewedAt)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationRequestCard;